"use client";

import React from "react";
import { Zap, Fingerprint, ShieldCheck, CreditCard, CircleCheck, ChevronRight, Apple } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function SeamlessCheckout() {
  const {
    ref: checkoutRef,
    isVisible: isCheckoutVisible
  } = useRevealOnScroll({
    threshold: 0.5,
    once: false
  });
  return <section id="seamless-checkout" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden">
      {}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-16 lg:mb-24">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
            <Zap className="size-4" />
            <span>The One-Tap Philosophy</span>
          </div>
          <h2 className="font-default text-4xl lg:text-5xl font-bold tracking-tight mb-6">
            Commerce without the friction.
          </h2>
          <p className="text-lg text-muted-foreground">
            OceanWallet transforms complex crypto payments into a familiar, immediate experience. 
            No technical setup, no confusing onboarding—just pure conversion.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center max-w-6xl mx-auto">
          
          {}
          <div className="lg:col-span-3 order-2 lg:order-1">
            <Card className="bg-card/40 backdrop-blur-md border-border/50 shadow-lg relative overflow-hidden group hover:border-primary/30 transition-colors">
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="size-14 rounded-2xl bg-secondary flex items-center justify-center mb-6 text-primary shadow-inner">
                  <Zap className="size-7" />
                </div>
                <h3 className="font-default text-4xl font-bold text-foreground mb-2 tracking-tight">0s</h3>
                <p className="text-muted-foreground font-medium">API Setup Required</p>
                <p className="text-sm text-muted-foreground/70 mt-4 leading-relaxed">
                  Tenant-based managed experience ready instantly.
                </p>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:col-span-6 order-1 lg:order-2 relative" ref={checkoutRef}>
            {}
            <div className="absolute -inset-1 bg-gradient-to-b from-primary/20 to-transparent rounded-3xl blur-xl opacity-50" />
            
            <Card className="relative bg-card border-border shadow-2xl rounded-2xl overflow-hidden h-[500px] flex flex-col">
              {}
              <div className="h-12 border-b border-border bg-secondary/50 flex items-center px-4 gap-4">
                <div className="flex gap-1.5">
                  <div className="size-3 rounded-full bg-muted-foreground/30" />
                  <div className="size-3 rounded-full bg-muted-foreground/30" />
                  <div className="size-3 rounded-full bg-muted-foreground/30" />
                </div>
                <div className="flex-1 flex justify-center">
                  <div className="h-5 w-32 bg-muted/50 rounded-md flex items-center justify-center gap-2 text-xs text-muted-foreground font-mono">
                    <ShieldCheck className="size-3" />
                    checkout.secure
                  </div>
                </div>
              </div>

              {}
              <div className="p-8 flex-1 flex flex-col">
                <div className="flex items-start justify-between mb-8">
                  <div>
                    <h4 className="font-default text-xl font-semibold mb-1">OceanWallet Pro</h4>
                    <p className="text-sm text-muted-foreground">Annual Subscription</p>
                  </div>
                  <span className="font-default text-xl font-bold">$99.00</span>
                </div>

                <div className="space-y-4 mb-8 flex-1">
                  <div className="space-y-2">
                    <div className="h-4 w-24 bg-muted rounded" />
                    <div className="h-10 w-full bg-input/50 rounded-md border border-border flex items-center px-3">
                      <span className="text-sm text-muted-foreground">user@example.com</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 w-32 bg-muted rounded" />
                    <div className="h-10 w-full bg-input/50 rounded-md border border-border flex items-center px-3 gap-2">
                      <CreditCard className="size-4 text-muted-foreground" />
                      <span className="text-sm text-muted-foreground">•••• •••• •••• 4242</span>
                    </div>
                  </div>
                </div>

                <Button className="w-full h-12 text-base font-semibold opacity-50 pointer-events-none">
                  Complete Purchase
                </Button>
              </div>

              {}
              <div className={`absolute inset-x-0 bottom-0 bg-secondary border-t border-border shadow-[0_-10px_40px_rgba(0,0,0,0.5)] rounded-t-3xl p-6 flex flex-col transition-transform duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] ${isCheckoutVisible ? "translate-y-0" : "translate-y-full"}`}>
                {}
                <div className="w-12 h-1.5 bg-muted-foreground/30 rounded-full mx-auto mb-6" />
                
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <div className="size-8 bg-foreground text-background rounded-full flex items-center justify-center">
                      <Apple className="size-5 fill-current" />
                    </div>
                    <span className="font-default font-semibold text-lg">Pay</span>
                  </div>
                  <span className="font-default text-2xl font-bold">$99.00</span>
                </div>

                <div className="bg-background rounded-xl p-4 mb-6 border border-border flex items-center gap-4">
                  <div className="size-10 bg-primary/10 rounded-full flex items-center justify-center text-primary shrink-0">
                    <CreditCard className="size-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">OceanWallet Crypto Card</p>
                    <p className="text-xs text-muted-foreground">USDC Balance: $1,250.00</p>
                  </div>
                  <ChevronRight className="size-4 text-muted-foreground" />
                </div>

                <div className="flex flex-col items-center justify-center gap-3 mt-2">
                  <div className="size-14 bg-primary/20 text-primary rounded-full flex items-center justify-center animate-pulse">
                    <Fingerprint className="size-8" />
                  </div>
                  <p className="text-sm text-muted-foreground font-medium">Double Click to Pay</p>
                </div>
              </div>
            </Card>
          </div>

          {}
          <div className="lg:col-span-3 order-3">
            <Card className="bg-card/40 backdrop-blur-md border-border/50 shadow-lg relative overflow-hidden group hover:border-primary/30 transition-colors">
              <div className="absolute inset-0 bg-gradient-to-bl from-primary/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
              <CardContent className="p-8 flex flex-col items-center text-center">
                <div className="size-14 rounded-2xl bg-secondary flex items-center justify-center mb-6 text-primary shadow-inner">
                  <Fingerprint className="size-7" />
                </div>
                <h3 className="font-default text-4xl font-bold text-foreground mb-2 tracking-tight">1-Tap</h3>
                <p className="text-muted-foreground font-medium">Authentication</p>
                <p className="text-sm text-muted-foreground/70 mt-4 leading-relaxed">
                  Secure identity verification bound directly to your device.
                </p>
              </CardContent>
            </Card>
          </div>

        </div>
        
        {}
        <div className="mt-16 flex flex-wrap justify-center gap-8 opacity-60">
          <div className="flex items-center gap-2 text-sm font-medium">
            <ShieldCheck className="size-4 text-primary" />
            <span>Bank-Grade Security</span>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <CircleCheck className="size-4 text-primary" />
            <span>Instant Settlement</span>
          </div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Zap className="size-4 text-primary" />
            <span>Zero Gas Fees</span>
          </div>
        </div>
      </div>
    </section>;
}